import { HttpStatusCode } from "axios";
import { Mahnung } from "../models/admin-management-entities";
import { getAdminAxiosInstance } from "./api-config";
import {
  CreateUpdateMahnungDTO,
  MahnstufeEnum,
} from "./dtos/admin-management-dtos";

/** Mahnung */
function transformMahnungResponse(m: any): Mahnung {
  return {
    ID: m.ID_Mahnung,
    ID_Mahnung: m.ID_Mahnung,
    ID_Belegung: m.ID_Belegung,
    ID_Kunde: m.kunde?.ID_Kunde ?? 0,
    Nachname: m.kunde?.Nachname ?? "",
    Vorname: m.kunde?.Vorname ?? "",
    Mahnfrist: m.Mahnfrist,
    Mahnstufe: m.Mahnstufe,
    Mahngebuhren: (m.mahngebuhren ?? []).map((g: any) => ({
      ...g,
      Gebuhr: parseFloat(g.Gebuhr),
    })),
    createdDate: m.createdDate,
    Summe: m.Summe,
    Status: m.Status,
    IstExportiert: m.IstExportiert,
    Notizen: m.Notizen,
  } as Mahnung;
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export async function getMahnung(idMahnung: number) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.get(`/mahnwesen/mahnung`, {
    params: {
      ID_Mahnung: idMahnung,
    },
  });

  if (response.data.status !== HttpStatusCode.Ok) {
    throw Error("Beim laden der Mahnung ist ein Fehler aufgetreten.");
  }

  const m = response.data.mahnung;

  const mahnungEntity: Mahnung = transformMahnungResponse(m);
  return mahnungEntity;
}

export async function getMahnungenByStatusList(statusList: number[]) {
  const axiosInstance = getAdminAxiosInstance();

  const response = await axiosInstance.get(`/mahnwesen/mahnungen`, {
    params: {
      status: statusList,
    },
    paramsSerializer: (params) => {
      const query = new URLSearchParams();
      params.status.forEach((s: number) => query.append("status", String(s)));
      return query.toString();
    },
  });

  console.log(response);

  if (response.data.status !== HttpStatusCode.Ok) {
    return { status: response.data.status, mahnungen: null };
  }

  const mahnungEntities: Mahnung[] = response.data.mahnungen.map((m: any) =>
    transformMahnungResponse(m)
  );

  return {
    status: response.data.status,
    mahnungen: mahnungEntities,
  };
}

export async function getManyMahnungen(offset: number, status?: number[]) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.get(`/mahnwesen`, {
    params: {
      offset: offset,
      status: status?.join(","),
    },
  });

  if (response.data.status !== HttpStatusCode.Ok) {
    return {}; // TODO Error like Maria
  }

  const mahnungEntities: Mahnung[] = response.data.mahnungen.map((m: any) => {
    return transformMahnungResponse(m);
  });

  return {
    status: response.data.status,
    mahnungen: mahnungEntities,
  };
}

export async function createMahnung(
  ID_Belegung: number,
  ID_Kunde: number,
  Gebuhr: number,
  Mahnstufe: MahnstufeEnum
) {
  const mahnungDto: CreateUpdateMahnungDTO = {
    ID_Belegung,
    ID_Kunde,
    Gebuhr,
    Mahnstufe,
  };
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.post(`/mahnwesen`, { mahnungDto });
  return {
    status: response.data.status,
    mahnung: response.data.mahnung,
  };
}

export async function incrementMahnung(ID_Mahnung: number) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.put(`/mahnwesen`, {
    ID_Mahnung: ID_Mahnung,
  });
  return {
    status: response.status,
    mahnung: response.data.mahnung,
  };
}

export async function resetMahnung(idMahnung: number) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.delete(`/mahnwesen`, {
    params: {
      ID_Mahnung: idMahnung,
    },
  });
  return response.status;
}

export async function setMahnungStatusPaid(idMahnung: number) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.put(`/mahnwesen/zahlung/${idMahnung}`);
  return response.status;
}

export async function uploadMahnungCsv(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const axiosInstance = getAdminAxiosInstance();
  const res = await axiosInstance.post("/mahnwesen/csv", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res;
}

export async function searchMahnungen(searchQuery: string, offset = 0) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.get(`/mahnwesen/search`, {
    params: {
      q: searchQuery,
      offset,
    },
  });

  if (response.data.status !== HttpStatusCode.Ok) {
    throw new Error("Fehler bei der Mahnwesen-Suche");
  }

  const mahnungEntities: Mahnung[] = response.data.results.map((m: any) =>
    transformMahnungResponse(m)
  );

  return {
    status: response.data.status,
    mahnungen: mahnungEntities,
  };
}

export async function exportMahnungExcel(idMahnungen: number[]): Promise<void> {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.post(
    `/mahnwesen/export-excel`,
    { ids: idMahnungen },
    {
      responseType: "blob", // <- wichtig für Datei-Download
    }
  );

  if (response.status !== HttpStatusCode.Created) {
    throw new Error("Fehler beim Erstellen der Excel-Datei.");
  }

  triggerDownload(new Blob([response.data]), `inkasso-export.xlsx`);
}

export async function updateMahnungNotizen(
  ID_Mahnung: number,
  Notizen: string
) {
  const axiosInstance = getAdminAxiosInstance();
  const response = await axiosInstance.put(`/mahnwesen/notiz`, {
    ID_Mahnung,
    Notizen,
  });

  if (response.status !== HttpStatusCode.Ok) {
    throw new Error("Error updating note");
  }

  return response.data;
}
